<template>
  <div
    class="block-draw-winner"
    :class="{
      'has-transitioned': runTransition,
      'is-resetting': isResetting,
    }"
    :style="`--reel-length: ${amountOfEntries - 1}`"
  >
    <div class="block-draw-winner__inner">
      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="block-draw-winner__entry"
      >
        <AtomTextHeading
          font-size="extra-big"
          :text="entry"
          alignment="center"
        />
      </div>
    </div>

    <Transition name="fade-in">
      <AtomButton
        v-if="!isSpinning"
        :is-disabled="isDisabled"
        class="block-draw-winner__button"
        :text="isDisabled ? noMoreSpinsText : spinText"
        @click="winner ? startSpinning() : emit('on-draw-winner')"
      />
    </Transition>
  </div>
</template>

<script setup>

const props = defineProps({
    isDisabled: {
        type: Boolean,
        default: false,
    },

    spinText: {
        type: String,
        default: 'Spin',
    },

    noMoreSpinsText: {
        type: String,
        default: 'Keine weiteren Gewinner möglich',
    },

    possibleWinners: {
        type: Array,
        default: () => [],
    },

    winner: {
        type: String,
        default: null,
    },

    amountOfEntries: {
        type: Number,
        default: 20,
    },

    startMessage: {
        type: String,
        default: 'Und gewonnen hat...?',
    },
});

const isSpinning = ref(false);
const runTransition = ref(false);
const isResetting = ref(false);
const { possibleWinners, winner } = toRefs(props);

/*
    Emit
*/
const emit = defineEmits(['on-draw-winner']);

/*
    Spin the wheel
*/
const startSpinning = async () => {
    const startAnimation = () => {
        isSpinning.value = true;
        runTransition.value = true;

        setTimeout(() => {
            isSpinning.value = false;
        }, 6000);
    };

    // If runTransition is true we have to pause the transition and reset the position
    if (runTransition.value) {
        isResetting.value = true;

        await sleep(10);
        runTransition.value = false;

        await sleep(10);
        isResetting.value = false;
        startAnimation();
    } else {
        startAnimation();
    }
};

/*
    Initialize Array of possible winners with winner as last element
*/
const entries = ref([props.startMessage]);

const initialize = (winnerToInitialize, possibleWinnersToInitialize) => {
    entries.value = [props.startMessage, ...possibleWinnersToInitialize];

    // If possible winners are less than amount of entries,
    // add random entries from cleanedPossibleWinners
    while (props.amountOfEntries - 1 > entries.value.length) {
        const randomIndex = Math.floor(
            Math.random() * possibleWinnersToInitialize.length,
        );
        entries.value.push(possibleWinnersToInitialize[randomIndex]);
    }

    // If possible winners are more than amount of entries,
    // remove random entries from cleanedPossibleWinners
    while (props.amountOfEntries - 1 < entries.value.length) {
        const randomIndex = Math.floor(
            Math.random() * entries.value.length,
        );
        entries.value.splice(randomIndex, 1);
    }

    // push winner as last element
    entries.value.push(winnerToInitialize);
};

initialize(winner.value, possibleWinners.value);

defineExpose({
    initialize,
    startSpinning,
});

/*
    Confettis
*/
const { triggerConfetti } = useConfetti();
watch(() => isSpinning.value, (newValue) => {
    if (!newValue) {
        triggerConfetti();
    }
});
</script>

<style lang="scss" scoped>
.block-draw-winner {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;

    --spinner-duration: 6s;
}

.block-draw-winner__inner {
    transition: transform var(--spinner-duration) var(--ease-out--slotmachine);

    .is-resetting & {
        transition: none;
    }

    .has-transitioned & {
        transform: translateY(calc(100vh * -1 * var(--reel-length)));

        .is-resetting & {
            transform: translateY(0);
        }
    }
}

.block-draw-winner__entry {
    @include fluid-simple('padding-left', 30px, 300px);
    @include fluid-simple('padding-right', 30px, 300px);

    display: flex;
    height: 100vh;
    height: 100dvh;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.block-draw-winner__button {
    position: absolute;
    bottom: 150px;
    left: 50%;
    transform: translateX(-50%);
}
</style>
